import {
  ABOUT_IMAGE,
  BOOKING_BACKGROUND,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  FLAG_DE,
  FLAG_EN,
  FLAG_TR,
  FLAG_AR,
  FLEET_BASE,
  SERVICES_BASE,
  FAQ_BASE,
  SOCIAL_LINKS,
  HERO_SLIDES,
  GOOGLE_MAPS_EMBED,
  GOOGLE_MAPS_OPEN
} from '../../vars/limosen';

export type I18nCode = 'en-US' | 'de-AT' | 'tr-TR' | 'ar-EG';

export function getI18nHomepage(code: I18nCode) {
  // ---------------------------------------------------------------------------
  // DE-AT
  // ---------------------------------------------------------------------------
  if (code === 'de-AT') {
    const strings = {
      TopNavFleet: 'Versicherungslösungen',
      TopNavReviews: 'Bewertungen',
      TopNavBookNow: 'Online abschließen',
      TopNavContact: 'Kontakt & Beratung',
      TopNavFollowUs: 'Folgen Sie uns',
      TopNavAlwaysReachable: 'Immer erreichbar',
      TopNavCityCountry: 'Wien, Österreich',
      TopNavAccount: 'Konto',
      TopNavLanguage: 'Sprache',
      LangTooltipDE: 'Deutsch',
      LangTooltipEN: 'English',
      LangTooltipTR: 'Türkçe',
      LangTooltipAR: 'العربية',
      LangModalTitle: 'Sprache wählen',
      LanguageGerman: 'Deutsch',
      LanguageEnglish: 'English',
      LanguageTurkish: 'Türkçe',
      LanguageArabic: 'العربية',

      ServicesTitle: 'Unsere Leistungen für Ihre Absicherung',
      ServicesSubtitle:
        'Beratung, Tarifvergleich und laufende Betreuung für Privatkunden, Eltern, Studierende und Reisende.',
      MoreDetails: 'Details ansehen',
      ServicesDetailsTitle: 'Leistungsdetails',

      FaqTitle: 'Häufige Fragen zur Krankenversicherung',
      FaqSubtitle:
        'Kurze Antworten zu Krankenversicherung, Aufenthalt in Österreich und Tierkrankenversicherung.',

      FleetTitle: 'Versicherungslösungen',
      FleetPassengersLabel: 'Typische versicherte Personen:',
      FleetLuggageLabel: 'Leistungsbausteine:',

      BookingTitle:
        'Krankenversicherung für Österreich – persönliche Beratung und Online-Abschluss.',
      BookingReachPhone: 'Anrufen',
      BookingAnd: 'oder',
      BookingReachEmail: 'E-Mail schreiben',
      ContactCta: 'Beratung anfragen',

      FeedbackTitle: 'Erfahrungen & Bewertungen',
      FeedbackSubtitle:
        'Sehen Sie Ihre Google Bewertung direkt auf der Karte.',
      FeedbackBoxTitle: 'Google Bewertung',
      FeedbackBoxText:
        'Öffnen Sie WIENVERS / freiearztwahl.at in Google Maps, um Sterne und Kommentare zu sehen oder eine Bewertung zu schreiben.',
      FeedbackMapsCta: 'In Google Maps öffnen',

      AboutTitle: 'Über WIENVERS',
      AboutP1:
        'WIENVERS ist eine unabhängige Mehrfachagentur in Wien. Wir arbeiten mit mehreren Versicherungspartnern zusammen und finden für Sie den passenden Schutz – statt nur ein einziges Produkt zu verkaufen.',
      AboutP2:
        'Unser Schwerpunkt liegt auf Krankenversicherungen für Menschen und Tiere: von Kindern und Familien über Studierende bis zu Reisenden und Privatkund:innen, die ihre gesetzliche Absicherung sinnvoll ergänzen möchten.',
      AboutP3:
        'In Österreich besteht Krankenversicherungspflicht. Private Krankenversicherungen werden mit zunehmendem Alter teurer, während Wahlärzte oft kürzere Wartezeiten und weniger Ansteckungsgefahr im Wartezimmer bieten. Deshalb lohnt es sich häufig, Kinder und Familie früh abzusichern. Wir erklären Ihre Möglichkeiten verständlich und ohne Fachchinesisch.',
      AboutImageAlt: 'Beratung zu Krankenversicherungen',

      BookNowCta: 'Beratung vereinbaren',

      AchivementCounterSatisfiedCustomers: 'Zufriedene Kunden',

      FooterTagline:
        'Mehrfachagentur für Krankenversicherung in Wien',
      FooterRights: 'Alle Rechte vorbehalten.'
    };

    const navLinks = [
      { label: 'Versicherungslösungen', href: '#fahrzeuge' },
      { label: 'Leistungen & Service', href: '#services' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Bewertungen', href: '#rezensionen' },
      { label: 'Kontakt', href: '?contact' }
    ];

    const services = SERVICES_BASE.map(s => {
      switch (s.id) {
        case 'airport-transfer':
          return {
            id: s.id,
            title: 'Reiseversicherung für Ihren Urlaub',
            image: s.image,
            paragraphs: [
              'Sie reisen nach Österreich oder in den Schengen-Raum und möchten im Krankheitsfall keine böse Überraschung erleben. Mit einer passenden Reise- oder Auslandskrankenversicherung sichern Sie Arztbesuche, Spitalsaufenthalte und einen möglichen Rücktransport ab – oft für wenig Geld.',
              'Über unseren Partner Care Concept können Sie viele Tarife direkt online abschließen. Auf Wunsch prüfen wir gemeinsam vorab, ob der gewählte Schutz zu Ihrer Reise, Ihrem Visum und Ihrem Budget passt.'
            ]
          };
        case 'city-tour':
          return {
            id: s.id,
            title: 'Studenten-Krankenversicherung für Studium in Österreich',
            image: s.image,
            paragraphs: [
              'Sie kommen zum Studieren nach Österreich oder absolvieren ein Auslandssemester. Für Aufenthaltstitel, Inskription und den Alltag brauchen Sie eine Krankenversicherung, die von den österreichischen Stellen anerkannt wird.',
              'Wir kennen die speziellen Studententarife und die Rahmenbedingungen der Krankenversicherungspflicht. Gemeinsam finden wir eine Lösung, die zur Dauer Ihres Studiums und zu Ihrem Budget passt – inklusive Online-Abschlusswegen über unseren Partner Care Concept.'
            ]
          };
        case 'private-driver-service':
          return {
            id: s.id,
            title: 'Kinder- und Familienkrankenversicherung',
            image: s.image,
            paragraphs: [
              'Kinder werden schnell krank, Wartezimmer sind oft voll und Wartezeiten lang. Mit einer zusätzlichen privaten Krankenversicherung und dem Zugang zu Wahlärzt:innen reduzieren Sie das Ansteckungsrisiko und erhalten rascher Termine.',
              'Da Krankenversicherungen mit dem Alter teurer werden, lohnt sich ein Einstieg häufig schon im Kindesalter. Wir zeigen Ihnen, welche Leistungen für Ihre Familie sinnvoll sind und wie Sie Kinder früh und sinnvoll absichern können.'
            ]
          };
        case 'corporate-services':
          return {
            id: s.id,
            title: 'Private Krankenversicherung & Wahlärzte in Österreich',
            image: s.image,
            paragraphs: [
              'In Österreich besteht Krankenversicherungspflicht. Wer nicht oder nicht ausreichend über die ÖGK versichert ist – etwa Selbständige, neu Zugezogene oder Personen mit speziellen Berufsverläufen – benötigt eine passende private Lösung.',
              'Als Mehrfachagent vergleichen wir für Sie die Angebote unserer Partner. Wir achten auf Wartezeiten, Leistungsausschlüsse und die langfristige Beitragsentwicklung, damit Ihre Krankenversicherung heute passt und auch in Zukunft tragbar bleibt.'
            ]
          };
        case 'city-to-city':
          return {
            id: s.id,
            title: 'Tierkrankenversicherung',
            image: s.image,
            paragraphs: [
              'Auch Tiere werden krank: Operationen, Diagnostik und Medikamente können schnell hohe Kosten verursachen. Mit einer Tierkrankenversicherung schützen Sie sich vor unerwarteten Rechnungen und ermöglichen Ihrem Tier die Behandlung, die es braucht.',
              'Wir beraten Sie zu Tarifen (z. B. von HanseMerkur) für Hunde, Katzen und andere Haustiere und erklären klar, welche Leistungen tatsächlich versichert sind – von Operationen bis zu Vorsorgeuntersuchungen.'
            ]
          };
        case 'international':
          return {
            id: s.id,
            title:
              'Internationale Krankenversicherung & längere Auslandsaufenthalte',
            image: s.image,
            paragraphs: [
              'Sie leben zeitweise im Ausland, wechseln zwischen Ländern oder planen einen längeren Aufenthalt in Österreich. In solchen Fällen reichen Standardlösungen oft nicht aus.',
              'Gemeinsam mit Partnern wie Care Concept finden wir internationale Krankenversicherungen, die mehrere Länder abdecken und dennoch die Anforderungen der österreichischen Behörden erfüllen.'
            ]
          };
        default:
          return {
            id: s.id,
            title: 'Beratung, Tarifvergleich & laufende Betreuung',
            image: s.image,
            paragraphs: [
              'Versicherung ist Vertrauenssache. Wir unterstützen Sie nicht nur beim Abschluss, sondern auch im laufenden Alltag – bei Leistungsanträgen, Vertragsänderungen oder im Schadensfall.',
              'Als Mehrfachagentur bleiben wir an Ihrer Seite, wenn sich Ihr Leben verändert: etwa durch Familienzuwachs, ein Studium im Ausland, einen neuen Job oder ein Haustier. Wir überprüfen regelmäßig, ob Ihr Versicherungsschutz noch zu Ihnen passt.'
            ]
          };
      }
    });

    const faq = FAQ_BASE.map(f => {
      switch (f.id) {
        case 'general-classes':
          return {
            question:
              'Warum macht eine private Krankenversicherung trotz Pflichtversicherung Sinn?',
            answer:
              'Die gesetzliche Krankenversicherung deckt in Österreich viel ab, aber nicht alles. Private Krankenversicherung kann etwa kürzere Wartezeiten bei Wahlärzt:innen, bessere Spitalsleistungen oder zusätzliche Services bieten. Gerade bei Kindern und jungen Erwachsenen sind die Beiträge oft deutlich günstiger als später im Leben.'
          };
        case 'airport-flow':
          return {
            question:
              'Ich komme zum Studieren oder Arbeiten nach Österreich – welche Krankenversicherung brauche ich?',
            answer:
              'Für Aufenthaltstitel, Studium und viele Beschäftigungen ist eine ausreichende Krankenversicherung Pflicht. Welche Lösung passt, hängt von Status, Herkunftsland und Dauer des Aufenthalts ab. Wir prüfen mit Ihnen, ob eine spezielle Studentenpolizze, eine private Vollversicherung oder eine Kombination aus gesetzlicher und privater Krankenversicherung sinnvoll ist.'
          };
        case 'kids-multilingual':
          return {
            question:
              'Ab welchem Alter lohnt sich eine zusätzliche Krankenversicherung für Kinder?',
            answer:
              'Je früher Sie Kinder versichern, desto günstiger sind in der Regel die Beiträge und desto besser sind mögliche Konditionsrechte für später. Zusätzlich profitieren Kinder von kürzeren Wartezeiten und weniger Ansteckungsgefahr beim Besuch von Wahlärzt:innen. Wir zeigen Ihnen, welche Lösungen sich für Ihre Familie rechnen.'
          };
        default:
          return {
            question:
              'Reicht meine bestehende Versicherung, wenn ich nur auf Urlaub nach Österreich komme?',
            answer:
              'Das hängt von Ihrem Herkunftsland und Ihrem aktuellen Versicherungsschutz ab. Oft ist eine zusätzliche Reise- oder Auslandskrankenversicherung sinnvoll, um Arztkosten, Spitalsaufenthalte und einen Rücktransport abzudecken. Wir helfen Ihnen, die Bedingungen Ihrer bestehenden Versicherung zu prüfen und eine passende Ergänzung zu finden – auch für Ihr Haustier, wenn es mitreist.'
          };
      }
    });

    // HanseMerkur Online-Antrag Tierkrankenversicherung
    const tierInsuranceOnline = {
      href: 'https://secure2.hansemerkur.de/ola-ui2/136/491?adnr=4912895&locale=de_AT&wt=POPUP',
      title: 'Tierkrankenversicherung direkt online abschließen',
      description:
        'Über unseren Partner HanseMerkur können Sie die Tierkrankenversicherung bequem online beantragen. Ihre persönliche Beratung bleibt natürlich bei WIENVERS.'
    };

    const fleet = FLEET_BASE.map(v => {
      switch (v.id) {
        case 'business-sedan':
          return {
            name: 'Privatkund:innen & Angestellte',
            category: 'Private Krankenversicherung',
            description:
              'Individuelle Tarife für Erwachsene, die sich mehr als nur die gesetzliche Grundversorgung wünschen – etwa Zugang zu Wahlärzt:innen oder verbesserte Leistungen im Spital.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'Jetzt anfragen'
          };
        case 'electric-sedan':
          return {
            name: 'Studierende & Gäste aus dem Ausland',
            category: 'Studentenversicherung',
            description:
              'Krankenversicherungslösungen für Personen, die zum Studium oder zur Ausbildung nach Österreich kommen – inklusive Spezialtarifen von Partnern wie Care Concept.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'Jetzt anfragen'
          };
        case 'first-class-sedan':
          return {
            name: 'Familien & Kinder',
            category: 'Kinder- & Familienversicherung',
            description:
              'Frühzeitige Absicherung für Kinder und Familien, damit Beiträge langfristig leistbar bleiben und Wartezeiten in Ordinationen verkürzt werden können.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'Jetzt anfragen'
          };
        default:
          return {
            name: 'Haustiere & Tierfreund:innen',
            category: 'Tierkrankenversicherung',
            description:
              'Krankenversicherung für Hunde, Katzen und andere Haustiere, damit im Ernstfall genug Budget für Diagnose und Behandlung bleibt.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: tierInsuranceOnline.href,
            ctaLabel: 'Jetzt online abschließen'
          };
      }
    });

    const footer = [
      {
        title: 'Angebot & Beratung',
        links: [
          { label: 'Versicherungslösungen', href: '#fahrzeuge' },
          { label: 'Leistungen & Service', href: '#services' },
          { label: 'FAQ', href: '#faq' },
          { label: 'Bewertungen', href: '#rezensionen' }
        ]
      },
      {
        title: 'Rechtliches',
        links: [
          { label: 'Impressum', href: '/impressum' },
          { label: 'Datenschutz', href: '/datenschutz' }
        ]
      },
      {
        title: 'Kontakt',
        links: [
          {
            label: CONTACT_PHONE,
            href: `https://api.whatsapp.com/send?phone=${encodeURIComponent(
              CONTACT_PHONE
            )}`
          },
          { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
          { label: 'Beratung anfragen', href: '/?contact' }
        ]
      }
    ];

    return {
      code,
      strings,
      navLinks,
      services,
      faq,
      fleet,
      footer,
      media: {
        ABOUT_IMAGE,
        BOOKING_BACKGROUND,
        HERO_SLIDES,
        FLAG_DE,
        FLAG_EN,
        FLAG_TR,
        FLAG_AR,
        GOOGLE_MAPS_EMBED,
        GOOGLE_MAPS_OPEN,
        SOCIAL_LINKS
      }
    };
  }

  // ---------------------------------------------------------------------------
  // TR-TR
  // ---------------------------------------------------------------------------
  if (code === 'tr-TR') {
    const strings = {
      TopNavFleet: 'Sigorta çözümleri',
      TopNavReviews: 'Yorumlar',
      TopNavBookNow: 'Online başvuru',
      TopNavContact: 'İletişim & danışmanlık',
      TopNavFollowUs: 'Bizi takip edin',
      TopNavAlwaysReachable: 'Her zaman ulaşılabilir',
      TopNavCityCountry: 'Viyana, Avusturya',
      TopNavAccount: 'Hesap',
      TopNavLanguage: 'Dil',
      LangTooltipDE: 'Deutsch',
      LangTooltipEN: 'English',
      LangTooltipTR: 'Türkçe',
      LangTooltipAR: 'العربية',
      LangModalTitle: 'Dil seçin',
      LanguageGerman: 'Deutsch',
      LanguageEnglish: 'English',
      LanguageTurkish: 'Türkçe',
      LanguageArabic: 'العربية',

      ServicesTitle: 'Sizin için sunduğumuz hizmetler',
      ServicesSubtitle:
        'Özel sağlık sigortası, çocuk ve öğrenci sigortaları, seyahat ve evcil hayvan sigortası için danışmanlık ve karşılaştırma.',
      MoreDetails: 'Detayları gör',
      ServicesDetailsTitle: 'Hizmet ayrıntıları',

      FaqTitle: 'Sık sorulan sorular',
      FaqSubtitle:
        'Avusturya’da sağlık sigortası, öğrenciler, aileler ve evcil hayvan sigortası hakkında kısa yanıtlar.',

      FleetTitle: 'Sigorta çözümleri',
      FleetPassengersLabel: 'Tipik sigortalılar:',
      FleetLuggageLabel: 'Teminat paketleri:',

      BookingTitle:
        'Avusturya için sağlık sigortası – bire bir danışmanlık veya online başvuru.',
      BookingReachPhone: 'Ara',
      BookingAnd: 'veya',
      BookingReachEmail: 'e-posta gönder',
      ContactCta: 'Danışmanlık talep et',

      FeedbackTitle: 'Deneyimler & yorumlar',
      FeedbackSubtitle:
        'Müşterilerimizin danışmanlık sürecimizi ve desteğimizi nasıl anlattığını görün.',
      FeedbackBoxTitle: 'Google puanı',
      FeedbackBoxText:
        'WIENVERS / freiearztwahl.at sayfamızı Google Haritalar’da açarak yıldızları ve yorumları görebilir veya kendi değerlendirmenizi yazabilirsiniz.',
      FeedbackMapsCta: 'Google Haritalar’da aç',

      AboutTitle: 'WIENVERS hakkında',
      AboutP1:
        'WIENVERS, Viyana’da faaliyet gösteren bağımsız bir çoklu sigorta acentesidir. Birçok tanınmış sigorta şirketi ile çalışıyor ve size tek bir ürünü satmak yerine ihtiyacınıza uygun çözümü bulmaya odaklanıyoruz.',
      AboutP2:
        'Uzmanlık alanımız, insanlar ve hayvanlar için sağlık sigortasıdır: çocuklar, aileler, Avusturya’da okumak isteyen öğrenciler, sadece tatil için gelen misafirler ve yasal sigortasını özel sigorta ile güçlendirmek isteyen herkes.',
      AboutP3:
        'Avusturya’da sağlık sigortası zorunludur. Özel sağlık sigortası yaş ilerledikçe daha pahalı hale gelir, buna karşılık anlaşmalı veya özel doktorlara erişim bekleme sürelerini kısaltır ve kalabalık bekleme odalarındaki enfeksiyon riskini azaltır. Bu nedenle özellikle çocuklar için erken yaşta sigorta yaptırmak mantıklıdır. Biz tüm seçenekleri sade ve anlaşılır bir dille açıklıyoruz.',
      AboutImageAlt: 'Sağlık sigortası danışmanlığı',

      BookNowCta: 'Danışmanlık randevusu al',

      AchivementCounterSatisfiedCustomers: 'Mutlu müşteriler',

      FooterTagline:
        'Viyana’da sağlık sigortası çoklu acentesi.',
      FooterRights: 'Tüm hakları saklıdır.'
    };

    const navLinks = [
      { label: 'Sigorta çözümleri', href: '#fahrzeuge' },
      { label: 'Hizmetler', href: '#services' },
      { label: 'SSS', href: '#faq' },
      { label: 'Yorumlar', href: '#rezensionen' },
      { label: 'İletişim', href: '?contact' }
    ];

    const services = SERVICES_BASE.map(s => {
      switch (s.id) {
        case 'airport-transfer':
          return {
            id: s.id,
            title: 'Tatiliniz için seyahat sigortası',
            image: s.image,
            paragraphs: [
              'Avusturya’ya veya Schengen bölgesine seyahat ediyor ve hastalık durumunda yüksek masraflarla karşılaşmak istemiyorsunuz. Doğru seyahat veya yurtdışı sağlık sigortası ile doktor ziyaretleri, hastane masrafları ve gerekirse ülkenize geri nakil güvence altına alınır.',
              'Care Concept ile özellikle öğrenciler ve turistler için birçok tarife online olarak başvurabilirsiniz. İsterseniz başvuruyu göndermeden önce poliçeyi birlikte inceliyor ve vizeniz ile planladığınız seyahate uygun olup olmadığını kontrol ediyoruz.'
            ]
          };
        case 'city-tour':
          return {
            id: s.id,
            title: 'Avusturya’da eğitim için öğrenci sağlık sigortası',
            image: s.image,
            paragraphs: [
              'Avusturya’da okumak veya bir dönem eğitim almak istiyorsunuz. Oturum izni, kayıt işlemi ve günlük yaşam için geçerli bir sağlık sigortasına ihtiyacınız var.',
              'Öğrenciler için özel olarak tasarlanmış tarifeleri ve Avusturya’daki sağlık sigortası zorunluluğunun şartlarını biliyoruz. Care Concept gibi partnerlerimizin öğrenci ürünleri sayesinde bütçenize ve öğrenim sürenize uygun, çoğu zaman online başvuru yapılabilen çözümler buluyoruz.'
            ]
          };
        case 'private-driver-service':
          return {
            id: s.id,
            title: 'Çocuk ve aile sağlık sigortası',
            image: s.image,
            paragraphs: [
              'Çocuklar sık hastalanır, bekleme odaları kalabalık ve randevu bulmak çoğu zaman zordur. Özel sağlık sigortası ve anlaşmalı doktorlara erişim sayesinde bekleme süreleri kısalabilir ve diğer çocuk hastalıklarına maruz kalma riski azalabilir.',
              'Sağlık sigortalarının primi yaşla birlikte yükseldiği için çocukları erken yaşta sigortalatmak genellikle daha ekonomiktir. Aileniz için hangi teminatların mantıklı olduğunu ve çocuk poliçelerinde nelere dikkat edilmesi gerektiğini açıkça anlatıyoruz.'
            ]
          };
        case 'corporate-services':
          return {
            id: s.id,
            title:
              'Avusturya’da özel sağlık sigortası ve anlaşmalı doktorlara erişim',
            image: s.image,
            paragraphs: [
              'Avusturya’da sağlık sigortası zorunludur. ÖGK üzerinden yeterli düzeyde sigortalı olmayan veya özel çözüme ihtiyaç duyan kişiler – örneğin serbest çalışanlar, yeni taşınanlar veya kariyerinde farklı ülkeler arasında geçiş yapanlar – uygun bir özel sigorta çözümüne ihtiyaç duyar.',
              'Çoklu acente olarak partner şirketlerimizin sunduğu tarifeleri sizin adınıza karşılaştırıyoruz. Bekleme sürelerine, kapsam dışı bırakılan durumlara ve uzun vadeli prim gelişimine bakarak bugün ve gelecekte karşılayabileceğiniz çözümler bulmanıza yardımcı oluyoruz.'
            ]
          };
        case 'city-to-city':
          return {
            id: s.id,
            title: 'Evcil hayvan sağlık sigortası',
            image: s.image,
            paragraphs: [
              'Hayvanlar da hastalanır. Ameliyatlar, tetkikler ve ilaçlar kısa sürede yüksek maliyetlere ulaşabilir. Evcil hayvan sağlık sigortası ile hem bütçenizi korur hem de dostunuzun ihtiyaç duyduğu tedaviyi almasını sağlarsınız.',
              'Köpek, kedi ve diğer evcil hayvanlar için HanseMerkur gibi şirketlerin farklı tarifelerini anlatıyor, hangi durumların gerçekten teminat altında olduğunu – ameliyatlardan rutin kontroller ve aşılamaya kadar – net bir şekilde açıklıyoruz.'
            ]
          };
        case 'international':
          return {
            id: s.id,
            title:
              'Uluslararası sağlık sigortası ve uzun süreli yurtdışı konaklamalar',
            image: s.image,
            paragraphs: [
              'Bir süre farklı ülkelerde yaşıyor, Avusturya’ya geçici olarak geliyorsunuz veya ülkeler arasında sık sık gidip geliyorsunuz. Bu durumlarda standart çözümler çoğu zaman yeterli olmaz.',
              'Care Concept gibi partnerlerimizle birlikte hem birden fazla ülkeyi kapsayan hem de Avusturya’daki resmi gerekliliklere uyum sağlayan uluslararası sağlık sigortası çözümleri sunuyoruz.'
            ]
          };
        default:
          return {
            id: s.id,
            title: 'Danışmanlık, karşılaştırma ve sürekli destek',
            image: s.image,
            paragraphs: [
              'Sigorta güven ilişkisidir. Sadece poliçe düzenlerken değil, hasar anında, teminat değişikliklerinde veya hayatınızdaki yeni dönemlerde de yanınızdayız.',
              'Aileniz büyüdüğünde, eğitim veya iş nedeniyle Avusturya’ya geldiğinizde ya da yeni bir evcil hayvan sahiplendiğinizde mevcut sigorta yapınızı birlikte gözden geçiriyor ve gerekirse uyarlıyoruz.'
            ]
          };
      }
    });

    const faq = FAQ_BASE.map(f => {
      switch (f.id) {
        case 'general-classes':
          return {
            question:
              'Avusturya’da zorunlu sigorta varken neden özel sağlık sigortası mantıklı?',
            answer:
              'Yasal sağlık sigortası Avusturya’da birçok masrafı karşılar, ancak her durumda yeterli olmayabilir. Özel sağlık sigortası, anlaşmalı veya özel doktorlara daha kısa bekleme süresiyle erişim, daha iyi hastane hizmetleri ve ek hizmetler sağlayabilir. Özellikle çocuklar ve genç yaşta sigortalanan kişiler için primler, ileri yaşlara göre genellikle daha uygundur.'
          };
        case 'airport-flow':
          return {
            question:
              'Avusturya’ya eğitim veya çalışma için geliyorum, hangi sağlık sigortasına ihtiyacım var?',
            answer:
              'Oturum izni, üniversite kaydı veya bazı iş sözleşmeleri için geçerli bir sağlık sigortası şarttır. Hangi çözümün uygun olduğu; statünüze, geldiğiniz ülkeye ve planladığınız süreye bağlıdır. Öğrencilere özel poliçeler, tam kapsamlı özel sigorta veya yasal ve özel sigortanın birlikte kullanıldığı hibrit çözümler söz konusu olabilir – bunları sizinle birlikte değerlendiriyoruz.'
          };
        case 'kids-multilingual':
          return {
            question:
              'Çocuklar için ek sağlık sigortası yaptırmak hangi yaştan itibaren mantıklı?',
            answer:
              'Primler yaşla birlikte yükseldiği için çocuklara erken yaşta ek sağlık sigortası yaptırmak çoğu zaman avantajlıdır. Böylece hem uzun vadede daha hesaplı bir çözüm elde edersiniz hem de çocuklarınız daha kısa bekleme süreleri ve kalabalık bekleme odalarında daha az enfeksiyon riski gibi avantajlardan faydalanabilir. Aileniz için hangi seçeneklerin uygun olduğunu birlikte planlıyoruz.'
          };
        default:
          return {
            question:
              'Sadece tatil için Avusturya’ya geliyorum, mevcut sigortam yeterli mi?',
            answer:
              'Bu durum mevcut sigorta kapsamınıza ve geldiğiniz ülkeye bağlıdır. Birçok durumda ek bir seyahat veya yurtdışı sağlık sigortası; doktor, hastane ve olası geri nakil masraflarını güvence altına almak için faydalıdır. Mevcut poliçenizi inceleyip, gerekirse Avusturya ve Schengen için uygun ek çözümler öneriyoruz – evcil hayvanınız sizinle geliyorsa, onun için de.'
          };
      }
    });

    const fleet = FLEET_BASE.map(v => {
      switch (v.id) {
        case 'business-sedan':
          return {
            name: 'Bireysel müşteriler ve çalışanlar',
            category: 'Özel sağlık sigortası',
            description:
              'Yasal sigortaya ek olarak daha kapsamlı koruma isteyen yetişkinler için; anlaşmalı doktorlar, daha iyi hastane hizmetleri ve ek teminatlar.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'Şimdi danışmanlık iste'
          };
        case 'electric-sedan':
          return {
            name: 'Öğrenciler ve yurt dışından gelen misafirler',
            category: 'Öğrenci sigortası',
            description:
              'Avusturya’da eğitim veya mesleki eğitim için bulunan kişiler için sağlık sigortası çözümleri – Care Concept gibi partnerlerimizin özel ürünleri dahil.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'Şimdi danışmanlık iste'
          };
        case 'first-class-sedan':
          return {
            name: 'Aileler ve çocuklar',
            category: 'Çocuk ve aile sigortası',
            description:
              'Çocuklar ve aileler için erken dönemde başlayan, uzun vadede ödenebilir kalması hedeflenen sağlık sigortası paketleri.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'Şimdi danışmanlık iste'
          };
        default:
          return {
            name: 'Evcil hayvanlar ve hayvanseverler',
            category: 'Evcil hayvan sağlık sigortası',
            description:
              'Köpek, kedi ve diğer evcil hayvanlar için sağlık sigortası – tanı ve tedavi masraflarına karşı koruma.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref:
              'https://secure2.hansemerkur.de/ola-ui2/136/491?adnr=4912895&locale=de_AT&wt=POPUP',
            ctaLabel: 'Şimdi online başvur'
          };
      }
    });

    const footer = [
      {
        title: 'Gezinme',
        links: [
          { label: 'Sigorta çözümleri', href: '#fahrzeuge' },
          { label: 'Hizmetler', href: '#services' },
          { label: 'SSS', href: '#faq' },
          { label: 'Yorumlar', href: '#rezensionen' }
        ]
      },
      {
        title: 'Hukuki bilgiler',
        links: [
          { label: 'Künye', href: '/impressum' },
          { label: 'Gizlilik', href: '/datenschutz' }
        ]
      },
      {
        title: 'İletişim',
        links: [
          {
            label: CONTACT_PHONE,
            href: `https://api.whatsapp.com/send?phone=${encodeURIComponent(
              CONTACT_PHONE
            )}`
          },
          { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
          { label: 'Bize yazın', href: '/?contact' }
        ]
      }
    ];

    return {
      code,
      strings,
      navLinks,
      services,
      faq,
      fleet,
      footer,
      media: {
        ABOUT_IMAGE,
        BOOKING_BACKGROUND,
        HERO_SLIDES,
        FLAG_DE,
        FLAG_EN,
        FLAG_TR,
        FLAG_AR,
        GOOGLE_MAPS_EMBED,
        GOOGLE_MAPS_OPEN,
        SOCIAL_LINKS
      }
    };
  }

  // ---------------------------------------------------------------------------
  // AR-EG
  // ---------------------------------------------------------------------------
  if (code === 'ar-EG') {
    const strings = {
      TopNavFleet: 'حلول التأمين',
      TopNavReviews: 'التقييمات',
      TopNavBookNow: 'شراء عبر الإنترنت',
      TopNavContact: 'الاتصال والاستشارة',
      TopNavFollowUs: 'تابعنا',
      TopNavAlwaysReachable: 'متاحون دائماً',
      TopNavCityCountry: 'فيينا، النمسا',
      TopNavAccount: 'الحساب',
      TopNavLanguage: 'اللغة',
      LangTooltipDE: 'Deutsch',
      LangTooltipEN: 'English',
      LangTooltipTR: 'Türkçe',
      LangTooltipAR: 'العربية',
      LangModalTitle: 'اختر اللغة',
      LanguageGerman: 'Deutsch',
      LanguageEnglish: 'English',
      LanguageTurkish: 'Türkçe',
      LanguageArabic: 'العربية',

      ServicesTitle: 'خدماتنا التأمينية لك',
      ServicesSubtitle:
        'استشارة ومقارنة ومرافقة مستمرة للتأمين الصحي للأفراد والأسر والطلاب والمسافرين والحيوانات الأليفة.',
      MoreDetails: 'عرض التفاصيل',
      ServicesDetailsTitle: 'تفاصيل الخدمة',

      FaqTitle: 'أسئلة شائعة حول التأمين الصحي',
      FaqSubtitle:
        'إجابات قصيرة عن التأمين الصحي في النمسا، إقامة الطلاب والأسر والتأمين الصحي للحيوانات الأليفة.',

      FleetTitle: 'حلول التأمين',
      FleetPassengersLabel: 'الأشخاص المؤمن عليهم عادةً:',
      FleetLuggageLabel: 'مجالات التغطية:',

      BookingTitle:
        'تأمين صحي لإقامتك في النمسا – استشارة شخصية أو إبرام عبر الإنترنت.',
      BookingReachPhone: 'اتصل',
      BookingAnd: 'أو',
      BookingReachEmail: 'أرسل بريدًا إلكترونيًا',
      ContactCta: 'طلب استشارة',

      FeedbackTitle: 'تجارب وتقييمات العملاء',
      FeedbackSubtitle:
        'اطلع على ما يقوله العملاء عن استشارتنا ومرافقتنا لهم على المدى الطويل.',
      FeedbackBoxTitle: 'تقييم Google',
      FeedbackBoxText:
        'افتح صفحة WIENVERS / freiearztwahl.at على خرائط Google للاطلاع على النجوم والتعليقات أو لكتابة تقييمك.',
      FeedbackMapsCta: 'فتح في خرائط Google',

      AboutTitle: 'نبذة عن WIENVERS',
      AboutP1:
        'WIENVERS هي وكالة تأمين متعددة الشركات في فيينا. نتعاون مع عدة شركات تأمين معروفة ونبحث عن الحل الأنسب لك بدلاً من بيع منتج واحد فقط.',
      AboutP2:
        'نحن متخصصون في التأمين الصحي للبشر والحيوانات: من الأطفال والعائلات إلى الطلاب والأشخاص الذين يزورون النمسا أو يحتاجون إلى تغطية إضافية بجانب التأمين الإلزامي.',
      AboutP3:
        'في النمسا يوجد إلزام قانوني بالتأمين الصحي. ومع التقدم في السن تصبح أقساط التأمين الخاص أعلى، بينما يتيح الوصول إلى الأطباء الاختياريين مواعيد أسرع ويقلل من خطر العدوى في غرف الانتظار المزدحمة. لذلك يكون من المفيد غالباً البدء في التأمين في سن مبكرة وخاصة للأطفال. نشرح لك الخيارات المتاحة بلغة بسيطة وواضحة.',
      AboutImageAlt: 'استشارة حول التأمين الصحي',

      BookNowCta: 'حجز موعد استشارة',

      AchivementCounterSatisfiedCustomers: 'عملاء راضون',

      FooterTagline:
        'وكالة تأمين متعددة الشركات للتأمين الصحي في فيينا',
      FooterRights: 'جميع الحقوق محفوظة.'
    };

    const navLinks = [
      { label: 'حلول التأمين', href: '#fahrzeuge' },
      { label: 'الخدمات', href: '#services' },
      { label: 'الأسئلة الشائعة', href: '#faq' },
      { label: 'التقييمات', href: '#rezensionen' },
      { label: 'الاتصال', href: '?contact' }
    ];

    const services = SERVICES_BASE.map(s => {
      switch (s.id) {
        case 'airport-transfer':
          return {
            id: s.id,
            title: 'تأمين سفر لعطلتك',
            image: s.image,
            paragraphs: [
              'إذا كنت تنوي السفر إلى النمسا أو إلى منطقة شنغن، فمن المهم أن لا تتحمل وحدك تكاليف العلاج في حال المرض. من خلال تأمين صحي مناسب للسفر يمكن تغطية زيارات الطبيب والإقامة في المستشفى وحتى الإعادة إلى الوطن عند الحاجة.',
              'من خلال شريكنا Care Concept يمكن إبرام العديد من العقود عبر الإنترنت، خاصة للطلاب والزوار. ونستطيع قبل ذلك مراجعة التعرفة معك للتأكد من أنها تناسب رحلتك ووضع التأشيرة والميزانية.'
            ]
          };
        case 'city-tour':
          return {
            id: s.id,
            title: 'تأمين صحي للطلاب للدراسة في النمسا',
            image: s.image,
            paragraphs: [
              'إذا كنت تخطط للدراسة في النمسا أو لفصل دراسي في الخارج، فستحتاج إلى تأمين صحي ساري المفعول لقبولك في الجامعة ولتصريح الإقامة.',
              'نحن نعرف التعرفات الخاصة بالطلاب وشروط التأمين الصحي الإلزامي في النمسا. مع شركائنا مثل Care Concept نساعدك في إيجاد حل يناسب مدة دراستك ووضعك المالي وغالباً بإمكانية إبرام العقد بالكامل عبر الإنترنت.'
            ]
          };
        case 'private-driver-service':
          return {
            id: s.id,
            title: 'تأمين صحي للأطفال والعائلات',
            image: s.image,
            paragraphs: [
              'الأطفال يمرضون كثيراً وغرف الانتظار تكون مزدحمة، كما أن مواعيد الأطباء قد تتطلب الانتظار الطويل. مع تأمين صحي خاص وإمكانية الوصول إلى أطباء اختياريين يمكن تقليل وقت الانتظار وتقليل خطر العدوى.',
              'لأن أقساط التأمين الخاص ترتفع مع العمر، فمن المنطقي غالباً البدء بالتأمين في سن مبكرة وخاصة للأطفال. نوضح لك ما هي التغطيات المفيدة لعائلتك وما يجب الانتباه إليه في عقود التأمين للأطفال.'
            ]
          };
        case 'corporate-services':
          return {
            id: s.id,
            title:
              'تأمين صحي خاص والوصول إلى الأطباء الاختياريين في النمسا',
            image: s.image,
            paragraphs: [
              'يوجد في النمسا إلزام قانوني بالتأمين الصحي. الأشخاص الذين لا تغطيهم منظمة التأمين الصحي ÖGK بشكل كاف أو يحتاجون إلى حل خاص – مثل العاملين لحسابهم الخاص أو القادمين الجدد – يحتاجون إلى تأمين صحي خاص مناسب.',
              'بصفتنا وكالة متعددة الشركات نقارن عروض شركائنا نيابة عنك وننظر إلى فترات الانتظار والاستثناءات وشروط الأقساط على المدى الطويل، حتى تحصل على تغطية تناسبك اليوم ويمكن تحملها في المستقبل أيضاً.'
            ]
          };
        case 'city-to-city':
          return {
            id: s.id,
            title: 'تأمين صحي للحيوانات الأليفة',
            image: s.image,
            paragraphs: [
              'الحيوانات الأليفة يمكن أن تمرض أو تحتاج إلى عملية جراحية، وهذه الحالات قد تكون مكلفة جداً. من خلال تأمين صحي للحيوانات الأليفة تحمي ميزانيتك وتضمن حصول حيوانك الأليف على الرعاية المناسبة.',
              'نشرح لك التعرفات المختلفة من شركائنا مثل HanseMerkur للكلاب والقطط وغيرها من الحيوانات الأليفة، ونوضح ما الذي يتم تغطيته فعلاً – من العمليات إلى الفحوصات الوقائية.'
            ]
          };
        case 'international':
          return {
            id: s.id,
            title: 'تأمين صحي دولي والإقامات الطويلة في الخارج',
            image: s.image,
            paragraphs: [
              'إذا كنت تعيش لفترة في أكثر من دولة أو تأتي إلى النمسا لفترة أطول، فغالباً لا تكفي الحلول القياسية للتأمين الصحي.',
              'بالاشتراك مع شركائنا مثل Care Concept نوفر لك حلول تأمين صحي دولي تغطي عدة دول وتلبي في الوقت نفسه متطلبات السلطات في النمسا.'
            ]
          };
        default:
          return {
            id: s.id,
            title: 'استشارة ومقارنة ومرافقة مستمرة',
            image: s.image,
            paragraphs: [
              'التأمين مسألة ثقة. نحن لا نرافقك فقط عند إبرام العقد، بل أيضاً في الحياة اليومية – عند تقديم المطالبات، وتعديل العقود أو عند تغيّر وضعك العائلي أو الوظيفي.',
              'سواء استقبلت أفراداً جدداً في العائلة، أو بدأت الدراسة أو العمل في النمسا، أو حصلت على حيوان أليف جديد، نراجع معك تغطيتك التأمينية ونقوم بتعديلها عند الحاجة.'
            ]
          };
      }
    });

    const faq = FAQ_BASE.map(f => {
      switch (f.id) {
        case 'general-classes':
          return {
            question:
              'لماذا قد أحتاج إلى تأمين صحي خاص رغم وجود التأمين الإلزامي في النمسا؟',
            answer:
              'التأمين الصحي الإلزامي يغطي الكثير من التكاليف في النمسا، لكنه لا يوفر دائماً كل ما تحتاجه. يمكن أن يوفر التأمين الصحي الخاص مواعيد أسرع عند الأطباء الاختياريين، وخدمات أفضل في المستشفى، ومزايا إضافية أخرى. كلما بدأ التأمين في سن أبكر – خاصة للأطفال – كانت الأقساط عادة أكثر ملاءمة على المدى الطويل.'
          };
        case 'airport-flow':
          return {
            question:
              'سأقيم في النمسا للدراسة أو العمل، أي نوع من التأمين الصحي أحتاج؟',
            answer:
              'للحصول على تصريح إقامة أو تسجيل في الجامعة أو بعض الوظائف تحتاج إلى تأمين صحي كاف. نوع التأمين المناسب يعتمد على وضعك القانوني وبلدك الأصلي ومدة الإقامة. نوضح لك ما إذا كانت هناك حاجة إلى تأمين خاص للطلاب أو تأمين صحي خاص كامل أو حل يجمع بين التأمين الإلزامي والخاص.'
          };
        case 'kids-multilingual':
          return {
            question:
              'من أي عمر يكون التأمين الصحي الإضافي للأطفال مفيداً؟',
            answer:
              'كلما تقدم العمر زادت أقساط التأمين الخاص. لذلك من المفيد غالباً البدء بحماية إضافية في سن مبكرة، وخاصة للأطفال، للاستفادة من أقساط أفضل وحقوق أفضل في المستقبل. بالإضافة إلى ذلك، يحصل الأطفال على مواعيد أسرع ويقضون وقتاً أقل في غرف الانتظار المزدحمة. نشرح لك الخيارات المناسبة لعائلتك بالتفصيل.'
          };
        default:
          return {
            question:
              'هل يكفي التأمين الحالي لدي إذا كنت آتي إلى النمسا للسياحة فقط؟',
            answer:
              'هذا يعتمد على نوع التأمين الذي لديك حالياً وعلى بلدك الأصلي. في كثير من الحالات يكون من المنطقي إضافة تأمين صحي للسفر لتغطية زيارات الطبيب والإقامة في المستشفى وإمكانية الإعادة إلى الوطن. يمكننا مراجعة تأمينك الحالي معك واقتراح حلول إضافية مناسبة – لك ولحيوانك الأليف إذا كان يسافر معك.'
          };
      }
    });

    const fleet = FLEET_BASE.map(v => {
      switch (v.id) {
        case 'business-sedan':
          return {
            name: 'العملاء الأفراد والموظفون',
            category: 'تأمين صحي خاص',
            description:
              'تغطية إضافية بجانب التأمين الإلزامي للأشخاص الذين يرغبون في خدمات أوسع، مثل الأطباء الاختياريين وخدمات أفضل في المستشفيات.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'اطلب استشارة الآن'
          };
        case 'electric-sedan':
          return {
            name: 'الطلاب والضيوف القادمون من الخارج',
            category: 'تأمين للطلاب',
            description:
              'حلول تأمين صحي للأشخاص الذين يأتون إلى النمسا للدراسة أو التدريب، بما في ذلك التعرفات الخاصة من شركائنا مثل Care Concept.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'اطلب استشارة الآن'
          };
        case 'first-class-sedan':
          return {
            name: 'العائلات والأطفال',
            category: 'تأمين للأطفال والعائلة',
            description:
              'تأمين صحي مبكر للأطفال والعائلات بهدف الحفاظ على أقساط يمكن تحملها على المدى الطويل وتقليل فترات الانتظار عند زيارة الأطباء.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref: '/?contact',
            ctaLabel: 'اطلب استشارة الآن'
          };
        default:
          return {
            name: 'الحيوانات الأليفة ومحبو الحيوانات',
            category: 'تأمين صحي للحيوانات الأليفة',
            description:
              'تأمين صحي للكلاب والقطط وغيرها من الحيوانات الأليفة حتى لا تشكل تكاليف العلاج عقبة أمام الرعاية الطبية اللازمة.',
            image: v.image,
            passengers: v.passengers,
            luggage: v.luggage,
            ctaHref:
              'https://secure2.hansemerkur.de/ola-ui2/136/491?adnr=4912895&locale=de_AT&wt=POPUP',
            ctaLabel: 'اشترِ التأمين الآن عبر الإنترنت'
          };
      }
    });

    const footer = [
      {
        title: 'التنقل',
        links: [
          { label: 'حلول التأمين', href: '#fahrzeuge' },
          { label: 'الخدمات', href: '#services' },
          { label: 'الأسئلة الشائعة', href: '#faq' },
          { label: 'التقييمات', href: '#rezensionen' }
        ]
      },
      {
        title: 'معلومات قانونية',
        links: [
          { label: 'بيانات الناشر', href: '/impressum' },
          { label: 'الخصوصية', href: '/datenschutz' }
        ]
      },
      {
        title: 'التواصل',
        links: [
          {
            label: CONTACT_PHONE,
            href: `https://api.whatsapp.com/send?phone=${encodeURIComponent(
              CONTACT_PHONE
            )}`
          },
          { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
          { label: 'اكتب لنا', href: '/?contact' }
        ]
      }
    ];

    return {
      code,
      strings,
      navLinks,
      services,
      faq,
      fleet,
      footer,
      media: {
        ABOUT_IMAGE,
        BOOKING_BACKGROUND,
        HERO_SLIDES,
        FLAG_DE,
        FLAG_EN,
        FLAG_TR,
        FLAG_AR,
        GOOGLE_MAPS_EMBED,
        GOOGLE_MAPS_OPEN,
        SOCIAL_LINKS
      }
    };
  }

  // ---------------------------------------------------------------------------
  // EN-US (default)
  // ---------------------------------------------------------------------------
  const strings = {
    TopNavFleet: 'Solutions',
    TopNavReviews: 'Reviews',
    TopNavBookNow: 'Buy online',
    TopNavContact: 'Contact & advice',
    TopNavFollowUs: 'Follow us',
    TopNavAlwaysReachable: 'Always reachable',
    TopNavCityCountry: 'Vienna, Austria',
    TopNavAccount: 'Account',
    TopNavLanguage: 'Language',
    LangTooltipDE: 'Deutsch',
    LangTooltipEN: 'English',
    LangTooltipTR: 'Türkçe',
    LangTooltipAR: 'العربية',
    LangModalTitle: 'Choose language',
    LanguageGerman: 'Deutsch',
    LanguageEnglish: 'English',
    LanguageTurkish: 'Türkçe',
    LanguageArabic: 'العربية',

    ServicesTitle: 'Our services for your cover',
    ServicesSubtitle:
      'Advice, comparison and ongoing support for private clients, parents, students and visitors in Austria.',
    MoreDetails: 'View details',
    ServicesDetailsTitle: 'Service details',

    FaqTitle: 'Frequently asked questions',
    FaqSubtitle:
      'Short answers on health insurance in Austria, students, families and pet insurance.',

    FleetTitle: 'Insurance solutions',
    FleetPassengersLabel: 'Typical insured persons:',
    FleetLuggageLabel: 'Benefit modules:',

    BookingTitle:
      'Health insurance for Austria – get personal advice or buy online.',
    BookingReachPhone: 'Call',
    BookingAnd: 'or',
    BookingReachEmail: 'send an email',
    ContactCta: 'Request advice',

    FeedbackTitle: 'Experiences & reviews',
    FeedbackSubtitle:
      'See how clients describe our advice and long-term support.',
    FeedbackBoxTitle: 'Google rating',
    FeedbackBoxText:
      'Open WIENVERS / freiearztwahl.at on Google Maps to see ratings and reviews or to leave your own feedback.',
    FeedbackMapsCta: 'Open in Google Maps',

    AboutTitle: 'About WIENVERS',
    AboutP1:
      'WIENVERS is an independent multi-agent based in Vienna. We cooperate with many well-known insurance companies and look for the solution that fits you instead of pushing just a single product.',
    AboutP2:
      'We are specialised in health insurance for people and animals: children, families, students coming to Austria and visitors who need additional or private cover.',
    AboutP3:
      'In Austria health insurance is mandatory. Private health insurance becomes more expensive with age, while access to elective doctors can shorten waiting times and reduce the risk of infections in crowded waiting rooms. That is why it often makes sense to start cover early for children and families. We explain your options clearly and without jargon.',
    AboutImageAlt: 'Health insurance advice',

    BookNowCta: 'Book a consultation',

    AchivementCounterSatisfiedCustomers: 'Satisfied customers',

    FooterTagline:
      'Health insurance multi-agent in Vienna.',
    FooterRights: 'All rights reserved.'
  };

  const navLinks = [
    { label: 'Solutions', href: '#fahrzeuge' },
    { label: 'Services', href: '#services' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Reviews', href: '#rezensionen' },
    { label: 'Contact', href: '?contact' }
  ];

  const services = SERVICES_BASE.map(s => {
    switch (s.id) {
      case 'airport-transfer':
        return {
          id: s.id,
          title: 'Travel insurance for your holiday',
          image: s.image,
          paragraphs: [
            'You are travelling to Austria or the Schengen area and want to avoid unexpected medical bills. With suitable travel or overseas health insurance, doctor visits, hospital stays and medical repatriation can be covered at a reasonable price.',
            'Through our partner Care Concept you can apply for many tariffs online, especially for students and visitors. If you like, we review the chosen tariff together before you submit your application.'
          ]
        };
      case 'city-tour':
        return {
          id: s.id,
          title: 'Student health insurance for studying in Austria',
          image: s.image,
          paragraphs: [
            'If you come to Austria for your studies or an exchange semester, you need valid health insurance that is accepted for enrolment and your residence permit.',
            'We know the specific student tariffs and the framework of the Austrian health insurance obligation. Together with partners such as Care Concept we help you find a solution that matches the duration of your stay and your budget – often with a fully online application process.'
          ]
        };
      case 'private-driver-service':
        return {
          id: s.id,
          title: 'Children and family health insurance',
          image: s.image,
          paragraphs: [
            'Children tend to get sick more often, waiting rooms are full and appointments can take a long time to get. With additional private health insurance and access to elective doctors, waiting times can be shorter and the risk of infections lower.',
            'Because private health insurance usually becomes more expensive with age, it can be attractive to start cover early, especially for children. We explain which benefits make sense for your family and what to consider when choosing policies for children.'
          ]
        };
      case 'corporate-services':
        return {
          id: s.id,
          title: 'Private health insurance and elective doctors in Austria',
          image: s.image,
          paragraphs: [
            'In Austria, health insurance is compulsory. People who are not sufficiently covered by the statutory system – for example self-employed persons, newcomers or people with international careers – need an appropriate private solution.',
            'As a multi-agent we compare the offers of our partner companies for you. We look at waiting periods, exclusions and long-term premium development so that you receive cover that fits your situation today and remains affordable in the future.'
          ]
        };
      case 'city-to-city':
        return {
          id: s.id,
          title: 'Pet health insurance',
          image: s.image,
          paragraphs: [
            'Pets can also fall ill or need surgery. Diagnostics, treatment and medication can quickly become expensive. Pet health insurance helps you manage these costs and still provide the treatment your animal needs.',
            'We advise you on different tariffs from partners such as HanseMerkur for dogs, cats and other pets and clearly explain which situations are actually covered – from operations to preventive care.'
          ]
        };
      case 'international':
        return {
          id: s.id,
          title: 'International health insurance and longer stays abroad',
          image: s.image,
          paragraphs: [
            'If you live in more than one country, stay in Austria for several months or move back and forth, standard solutions are often not sufficient.',
            'Together with partners like Care Concept we arrange international health insurance that covers several countries while still meeting the requirements of Austrian authorities.'
          ]
        };
      default:
        return {
          id: s.id,
          title: 'Ongoing advice, comparison and support',
          image: s.image,
          paragraphs: [
            'Insurance is a matter of trust. We support you not only when you sign a contract but also in everyday life – with claims, contract changes and when your life situation changes.',
            'When your family grows, you start studying or working in Austria or you get a new pet, we review your existing cover together and adjust it if needed.'
          ]
        };
    }
  });

  const faq = FAQ_BASE.map(f => {
    switch (f.id) {
      case 'general-classes':
        return {
          question:
            'Why does private health insurance make sense if Austria has mandatory cover?',
          answer:
            'Statutory health insurance in Austria covers a lot but not everything. Private health insurance can provide faster access to elective doctors, better hospital benefits and additional services. Starting cover early, especially for children and young adults, often leads to more favourable premiums in the long run.'
        };
      case 'airport-flow':
        return {
          question:
            'I am coming to Austria to study or work – what health insurance do I need?',
          answer:
            'For residence permits, university enrolment and many employment situations, sufficient health insurance is mandatory. The right solution depends on your status, home country and length of stay. We help you decide whether a student-specific policy, full private cover or a combination with statutory insurance is appropriate.'
        };
      case 'kids-multilingual':
        return {
          question:
            'From what age is additional health insurance for children useful?',
          answer:
            'Because premiums usually increase with age, it can be advantageous to arrange additional health cover early in life, especially for children. Children also benefit from shorter waiting times and less exposure to infections in crowded waiting rooms. We help you plan which options make sense for your family.'
        };
      default:
        return {
          question:
            'Is my existing insurance enough if I only visit Austria for a holiday?',
          answer:
            'This depends on your current insurance and your home country. In many cases an additional travel or overseas health insurance is recommended to cover doctor visits, hospital stays and possible repatriation. We review your existing policy with you and suggest suitable additional solutions – for you and, if needed, your pet.'
        };
    }
  });

  const fleet = FLEET_BASE.map(v => {
    switch (v.id) {
      case 'business-sedan':
        return {
          name: 'Private clients and employees',
          category: 'Private health insurance',
          description:
            'Additional cover on top of the statutory system for people who want more – such as elective doctors, better hospital benefits and extra services.',
          image: v.image,
          passengers: v.passengers,
          luggage: v.luggage,
          ctaHref: '/?contact',
          ctaLabel: 'Request advice now'
        };
      case 'electric-sedan':
        return {
          name: 'Students and guests from abroad',
          category: 'Student insurance',
          description:
            'Health insurance solutions for people who come to Austria to study or train, including special products from partners such as Care Concept.',
          image: v.image,
          passengers: v.passengers,
          luggage: v.luggage,
          ctaHref: '/?contact',
          ctaLabel: 'Request advice now'
        };
      case 'first-class-sedan':
        return {
          name: 'Families and children',
          category: 'Children and family insurance',
          description:
            'Early health cover for children and families with the aim of keeping premiums affordable over the long term and reducing waiting times for medical appointments.',
          image: v.image,
          passengers: v.passengers,
          luggage: v.luggage,
          ctaHref: '/?contact',
          ctaLabel: 'Request advice now'
        };
      default:
        return {
          name: 'Pets and pet owners',
          category: 'Pet health insurance',
          description:
            'Health insurance for dogs, cats and other pets so that medical treatment is not limited by unexpected costs.',
          image: v.image,
          passengers: v.passengers,
          luggage: v.luggage,
          ctaHref:
            'https://secure2.hansemerkur.de/ola-ui2/136/491?adnr=4912895&locale=de_AT&wt=POPUP',
          ctaLabel: 'Apply online now'
        };
    }
  });

  const footer = [
    {
      title: 'Explore',
      links: [
        { label: 'Solutions', href: '#fahrzeuge' },
        { label: 'Services', href: '#services' },
        { label: 'FAQ', href: '#faq' },
        { label: 'Reviews', href: '#rezensionen' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Imprint', href: '/impressum' },
        { label: 'Privacy', href: '/datenschutz' }
      ]
    },
    {
      title: 'Contact',
      links: [
        {
          label: CONTACT_PHONE,
          href: `https://api.whatsapp.com/send?phone=${encodeURIComponent(
            CONTACT_PHONE
          )}`
        },
        { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
        { label: 'Write to us', href: '/?contact' }
      ]
    }
  ];

  return {
    code,
    strings,
    navLinks,
    services,
    faq,
    fleet,
    footer,
    media: {
      ABOUT_IMAGE,
      BOOKING_BACKGROUND,
      HERO_SLIDES,
      FLAG_DE,
      FLAG_EN,
      FLAG_TR,
      FLAG_AR,
      GOOGLE_MAPS_EMBED,
      GOOGLE_MAPS_OPEN,
      SOCIAL_LINKS
    }
  };
}
